import React from 'react';
import { Box } from '@mui/material';

import { Typography } from '@occc-app/design-system';

import { State as AudiencesState } from '../../Audiences/redux';

const ageWithinSummary = (ageWithin: AudiencesState['ageWithin']): JSX.Element | null => {
  if (!ageWithin.active) {
    return null;
  }

  const { minimumAge, minimumAgeActive, maximumAge, maximumAgeActive } = ageWithin;

  const ageText = () => {
    if (minimumAgeActive && maximumAgeActive) {
      return `This offer is available for memberships between the ages of ${minimumAge} and ${maximumAge}.`;
    } else if (minimumAgeActive) {
      return `This offer is available for memberships age ${minimumAge} and older.`;
    } else if (maximumAgeActive) {
      return `This offer is available for memberships age ${maximumAge} and younger.`;
    } else {
      return 'No age range has been configured for the Age Within constraint.';
    }
  };

  return <Typography paragraph>{ageText()}</Typography>;
};

const inAllSegmentsSummary = (
  inAllSegments: AudiencesState['inAllSegments'],
  inAnySegments: AudiencesState['inAnySegments'],
): JSX.Element | null => {
  if (!inAllSegments.active) {
    return null;
  } else if (inAllSegments.active && inAnySegments.active) {
    return null;
  }

  return (
    <Typography paragraph>
      This offer should only be visible to memberships that are assigned to all of the following
      segments: <b>{inAllSegments.segments.join(', ')}</b>.
    </Typography>
  );
};

const inAnySegmentsSummary = (
  inAnySegments: AudiencesState['inAnySegments'],
  inAllSegments: AudiencesState['inAllSegments'],
): JSX.Element | null => {
  if (!inAnySegments.active) {
    return null;
  } else if (inAnySegments.active && inAllSegments.active) {
    return null;
  }

  return (
    <Typography paragraph>
      This offer should only be visible to memberships that are assigned to any of the following
      segments: <b>{inAnySegments.segments.join(', ')}</b>.
    </Typography>
  );
};

const inAllAndAnySegmentsSummary = (
  inAnySegments: AudiencesState['inAnySegments'],
  inAllSegments: AudiencesState['inAllSegments'],
): JSX.Element | null => {
  if (inAnySegments.active && inAllSegments.active) {
    return (
      <Typography paragraph>
        This offer should only be visible to memberships that are assigned to all of the following
        segments: <b>{inAllSegments.segments.join(', ')}</b> AND any of the following segments:{' '}
        <b>{inAnySegments.segments.join(', ')}</b>.
      </Typography>
    );
  } else {
    return null;
  }
};

interface IProps {
  audiences: AudiencesState;
}

export const AudiencesSummary: React.FC<IProps> = ({ audiences }) => {
  const { ageWithin, inAllSegments, inAnySegments } = audiences;

  return (
    <Box>
      <Box>{ageWithinSummary(ageWithin)}</Box>
      <Box>{inAllSegmentsSummary(inAllSegments, inAnySegments)}</Box>
      <Box>{inAnySegmentsSummary(inAnySegments, inAllSegments)}</Box>
      <Box>{inAllAndAnySegmentsSummary(inAnySegments, inAllSegments)}</Box>
    </Box>
  );
};
